/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018 - 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define(['./APIExtension', './APIContext', '../utils/MemUtil', 'underscore'], function (APIExtension, APIContext, MemUtil, _) {

	const ANONYMOUS_IMPL_TYPE = 'anonymous';

	const emptyFunction = function() {};
	const implKey = {};
	const loggerKey = {};

	// const ON = function() { return { remove: function() {} };};
	// const OFF = function() {};

	class APIInstance {
		constructor(type, implType) {

			Object.defineProperty(this, 'getInterfaceType', {
				configurable: false,
				value: function () {
					return type;
				}
			});
			Object.defineProperty(this, 'getImplType', {
				configurable: false,
				value: function () {
					return implType;
				}
			});
		}
	}

	const getAPIextensions = function (implementation) {
		return APIExtension.getAPIExtension(implementation);
	};

	/*
	 * Create a function in the object that is mapped to the same function in the implementation
	 */
	const createFunction = function (object, implementation, functionName, options = {}) {

		// Use a weakmap to avoid memory leak if someone holds on the API instance.
		// eslint-disable-next-line no-undef
		const params = new WeakMap();
		params.set(implKey, implementation);
		params.set(loggerKey, options.logger);
		const isDeprecated = options.isDeprecated;

		// Create the API extensions. This might append addition method to the implementation intances (e.g. on/off for event support)
		const apiExtensions = getAPIextensions(implementation);
		const functionImpl = implementation[functionName];
		if (!functionImpl) {
			throw new Error(`The method "${functionName}" is not part of the provided implementation`);
		}
		if (typeof (functionImpl) !== 'function') {
			throw new Error(`The method "${functionName}" is not a function in the provided implementation`);
		}

		if (isDeprecated) {
			object[functionName] = function () {

				// Throw an exception to capture the stack to be logged.
				try {
					throw new Error();
				} catch (e) {
					(params.get(loggerKey) || console).log(`The function "${functionName}" of the API "${object.getInterfaceType()}" is deprecated`, {
						callStack: e.stack
					});
				}
				const impl = params.get(implKey);
				if ( impl ) {
					try {
						return impl[functionName].apply( impl, arguments);
					} catch (error){
						if (!impl || impl.__api__destroyed){
							throw new Error('Access to a destroyed API instance');
						}
						throw error;
					}
				}
			};
		} else {

			if (apiExtensions.isEnabled() && apiExtensions.isSupported(functionName)) {
				object[functionName] = function () {
					let args = [...arguments];
					let apiContext;
					if (args[0] instanceof APIContext){
						apiContext = args[0];
						args.shift();
					}
					const extensionBeforeOptions = apiExtensions.onBeforeAction(functionName, args, apiContext);
					const impl =  params.get(implKey);
					let returnValue;
					try {
						returnValue = impl[functionName].apply( impl, args);
					} catch (error){
						if (!impl || MemUtil.isDestroyed(impl)){
							throw new Error('Access to a destroyed API instance');
						}
						throw error;
					}
					if (returnValue instanceof Promise) {
						returnValue.then((value) => {
							apiExtensions.onAfterAction(functionName, args, value, extensionBeforeOptions, apiContext);
						});
					} else {
						apiExtensions.onAfterAction(functionName, args, returnValue, extensionBeforeOptions, apiContext);
					}
					return returnValue;
				};
			} else {
				object[functionName] = function() {
					const impl =  params.get(implKey);
					return impl[functionName].apply(impl, arguments);
				};
			}
		}

	};


	/*
	 * Create a read only property in the give object that point the same property in the implementation
	 */
	const createReadOnlyProperty = function (object, propertyName, value) {
		Object.defineProperty(object, propertyName, {
			value: value,
			writable: false
		});
	};

	/*
	 * extend a give API instance to have all the function in the give interface. The functions will point to the same function in the implementation
	 */
	const extendAPI = function (apiInstance, implementation, apiInterface, options) {

		if (typeof (apiInterface) !== 'function') {
			throw new Error('The interface is not a class');
		}

		// implementation.on = ON;
		// implementation.off= OFF;

		const apiExtensions = getAPIextensions(implementation);
		apiExtensions.extendAPI(apiInstance, apiInterface);


		// Provide or override a destroy function that will beb used to clear both the implementation and API instandes
		// This is to avoid memory leaks if someone holds on to either instance after being destroyed.
		if (!implementation.destroy){
			implementation.destroy = emptyFunction;
		}

		if (typeof(implementation.destroy) === 'function' && !implementation.____destroy____) {
			// Use a weakmap to avoid memory leak if someone holds on the API instance.
			// eslint-disable-next-line no-undef
			const params = new WeakMap();
			const implKey = {};
			const apiKey = {};
			params.set(implKey, implementation);
			params.set(apiKey, apiInstance);
			// back up the original
			implementation.____destroy____ = implementation.destroy;
			implementation.destroy = function() {
				const impl = params.get(implKey);
				const api = params.get(apiKey);

				// call the original destroy
				impl.____destroy____.apply(impl, arguments);
				if (api){
					// clear the api instance contenta
					MemUtil.destroy(api);
				}
				if (impl){
					// clear the implementation instance content
					MemUtil.destroy(impl);
				}

			};
		}

		// Copy all the properties (function and non function) from the interface to the apiInstance
		const properties = Object.getOwnPropertyNames(apiInterface.prototype);
		for( let i=0; i<properties.length; i++) {
			let property = properties[i];
			if (typeof (apiInterface.prototype[property]) === 'function') {
				// Skip the constructor
				if (property !== 'constructor') {
					createFunction(apiInstance, implementation, property, options);
				}
			}
		}

		// Add static properties to the API instance for convenience
		const nativeProperties = Object.getOwnPropertyNames(class {});
		const keys = Object.keys(apiInterface);
		const staticKeys = _.filter(Object.getOwnPropertyNames(apiInterface), (p) => nativeProperties.indexOf(p) < 0);
		keys.push(...staticKeys);
		for(let j=0;j<keys.length; j++) {
			let key = keys[j];
			if (typeof (apiInterface[key]) !== 'function') {
				createReadOnlyProperty(apiInstance, key, apiInterface[key]);
			}
		}

		// the options might specify some implementation properties to copy to the API as well
		// This is just to handle deprecated interfaces like the visAPI where we want to the ownerWidget for example to be availale
		if (options.deprecatedPropList) {
			for(let k=0;k<options.deprecatedPropList.length;k++) {
				let prop = options.deprecatedPropList[k];
				createReadOnlyProperty(apiInstance, prop, implementation[prop]);
			}
		}

	};

	const createAPIInstance = function (type, implType) {
		return new APIInstance(type.name, implType);
	};

	/**
	 *
	 *  API factory used to create API instances based on a given implementation and a list of interfaces that the API should be restricted to
	 *
	 *
	 * @class APIFactory
	 */
	class APIFactory {

		setParentChildRelation(parent, child, options) {
			// Ensure the child extension are registeres
			const childAPIExtensions = getAPIextensions(child);

			const propagationInfo = options && options.propagationInfo;
			// Now access the parent event support and enable event propagation for the child
			const parentAPIExtensions = getAPIextensions(parent);
			const parentEventSupport = parentAPIExtensions.getSupport('events');
			if (parentEventSupport && childAPIExtensions.getSupport('events')) {
				parentEventSupport.enableChildEventPropagation(child, propagationInfo);
			}
		}

		/**
		 *
		 * Create an API instance using the provided implementation and the list of interfaces
		 *
		 *
		 * @param {Object} implementation - the API implementation class
		 * @param {Class[]} interfaceList
		 * @param {Object} options - Options to control how the API is created.
		 *  options.deprecatedPropList  list of the implementation properties to be copied to the API instance. This should only be used with deprecated APIs that contain properties.
		 *  options.isDeprecated - Mark this API as deprecated.. a console log will be logged with a stack trace
		 *  options.logger - Logger where the deprecated message will be logged. If not specified the console will be used.
		 *
		 * @return {Object} API instance where the instance contains only the function available in the interface list.
		 */
		createAPI(implementation, interfaceList, options) {

			if (!interfaceList || interfaceList.length === 0) {
				throw new Error('An interface must be defined');
			}

			// Create the API extensions
			getAPIextensions(implementation);

			const implType = implementation.constructor ? implementation.constructor.name : ANONYMOUS_IMPL_TYPE;
			const apiInstance = createAPIInstance(interfaceList[0], implType);

			for( let i=0; i< interfaceList.length; i++) {
				let apiInterface = interfaceList[i];
				extendAPI(apiInstance, implementation, apiInterface, options || {});
			}
			return apiInstance;
		}

		/**
		 * Refresh an existing API with the provided implementation and the list of interfaces
		 * @param {APIInstance} apiInstance API instance that needs to be refreshed
		 * @param {Object[]} implementations Array of API implementation instances
		 * @return {Object} refreshed API instance
		 */
		refreshAPI(apiInstance, implementations) {
			if (apiInstance instanceof APIInstance === false) {
				throw new Error('An API instance must be created');
			}

			// Create the API extensions. This might append addition method to the implementation intances (e.g. on/off for event support)
			implementations.forEach((implementation) => {
				getAPIextensions(implementation);
			});

			// replace all existing apis in the instance with the new implementation
			const properties = Object.getOwnPropertyNames(apiInstance);
			properties.forEach((property) => {
				const {
					configurable
				} = Object.getOwnPropertyDescriptor(apiInstance, property);
				if (configurable) {
					const correctImpl = _.find(implementations, (implementation) => {
						return typeof (implementation[property]) === 'function';
					});
					if (!correctImpl) {
						throw new Error(`The method "${property}" is not part of the provided implementations`);
					}

					createFunction(apiInstance, correctImpl, property);
				}
			});

			return apiInstance;
		}

		/**
		 * Create an API instance from a array of interface/implementation specs
		 *
		 * @param {Object []} implementationSpecList
		 *  spec.implementation - Implementation class
		 *  spec.interface - Interface class
		 *  spec.options - API creation options
		 *  options.deprecatedPropList  list of the implementation properties to be copied to the API instance. This should only be used with deprecated APIs that contain properties.
		 *  spec.options.isDeprecated - Mark this API as deprecated.. a console log will be logged with a stack trace
		 *  spec.options.logger - Logger where the deprecated message will be logged. If not specified the console will be used.
		 *
		 * @returns  {Object} API instance where the instance contains only the function available in the interface list.
		 *
		 */
		createAPIFromSpec(implementationSpecList) {
			if (!implementationSpecList || implementationSpecList.length === 0) {
				throw new Error('A spec must be defined');
			}

			const implType = implementationSpecList.length > 0 && implementationSpecList[0].implementation.constructor ? implementationSpecList[0].implementation.constructor.constructor.name : ANONYMOUS_IMPL_TYPE;
			const apiInstance = createAPIInstance(implementationSpecList[0].interface, implType);

			implementationSpecList.forEach((spec) => {
				// Create the API extensions. This might append addition method to the implementation intances (e.g. on/off for event support)
				getAPIextensions(spec.implementation);
				extendAPI(apiInstance, spec.implementation, spec.interface, spec.options || {});
			});
			return apiInstance;
		}

	}

	return new APIFactory();
});
